package de.xtl.retroguide.question

import androidx.compose.animation.animateColorAsState
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import de.xtl.retroguide.protocol.MAX_ROTI
import de.xtl.retroguide.protocol.MIN_ROTI
import de.xtl.retroguide.protocol.RadarAnswer
import de.xtl.retroguide.protocol.RadarDimension
import de.xtl.retroguide.protocol.toRadarAnswer
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource
import retroguide.app.shared.generated.resources.*

internal val RadarDimension.emoji: String
    get() = when (this) {
        RadarDimension.FUN -> "🎉"
        RadarDimension.SPEED -> "🚀"
        RadarDimension.QUALITY -> "💎"
        RadarDimension.COLLABORATION -> "🤝"
        RadarDimension.LEARNING -> "📚"
        RadarDimension.FOCUS -> "🎯"
    }

private val RadarDimension.labelRes: StringResource
    get() = when (this) {
        RadarDimension.FUN -> Res.string.radar_fun
        RadarDimension.SPEED -> Res.string.radar_speed
        RadarDimension.QUALITY -> Res.string.radar_quality
        RadarDimension.COLLABORATION -> Res.string.radar_collaboration
        RadarDimension.LEARNING -> Res.string.radar_learning
        RadarDimension.FOCUS -> Res.string.radar_focus
    }

private val RadarDimension.hintRes: StringResource
    get() = when (this) {
        RadarDimension.FUN -> Res.string.radar_fun_hint
        RadarDimension.SPEED -> Res.string.radar_speed_hint
        RadarDimension.QUALITY -> Res.string.radar_quality_hint
        RadarDimension.COLLABORATION -> Res.string.radar_collaboration_hint
        RadarDimension.LEARNING -> Res.string.radar_learning_hint
        RadarDimension.FOCUS -> Res.string.radar_focus_hint
    }

@Composable
internal fun RadarDimension.label() = stringResource(labelRes)

@Composable
internal fun RadarAnswerPicker(
    value: String,
    enabled: Boolean,
    onValueChange: (String) -> Unit,
) {
    val draft = value.toRadarAnswer() ?: RadarAnswer(emptyMap())
    Text(stringResource(Res.string.radar_answer_prompt), style = MaterialTheme.typography.titleMedium)
    RadarDimension.entries.forEach { dimension ->
        val selected = draft.scores[dimension]
        Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    "${dimension.emoji} ${dimension.label()}",
                    modifier = Modifier.weight(1f),
                    style = MaterialTheme.typography.titleSmall,
                )
                Text(
                    selected?.toString() ?: "–",
                    style = MaterialTheme.typography.titleSmall,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.primary,
                )
            }
            Text(
                stringResource(dimension.hintRes),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                (MIN_ROTI..MAX_ROTI).forEach { score ->
                    ScoreDot(
                        score = score,
                        filled = selected != null && score <= selected,
                        enabled = enabled,
                        description = stringResource(Res.string.radar_score_description, dimension.label(), score),
                        onClick = { onValueChange(draft.with(dimension, score).encode()) },
                    )
                }
            }
        }
    }
}

@Composable
private fun ScoreDot(
    score: Int,
    filled: Boolean,
    enabled: Boolean,
    description: String,
    onClick: () -> Unit,
) {
    val container by animateColorAsState(
        if (filled) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.surfaceVariant,
    )
    val content by animateColorAsState(
        if (filled) MaterialTheme.colorScheme.onPrimary else MaterialTheme.colorScheme.onSurfaceVariant,
    )
    Surface(
        onClick = onClick,
        enabled = enabled,
        shape = CircleShape,
        color = container,
        contentColor = content,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        modifier = Modifier.size(40.dp).semantics { contentDescription = description },
    ) {
        Row(horizontalArrangement = Arrangement.Center, verticalAlignment = Alignment.CenterVertically) {
            Text(score.toString(), style = MaterialTheme.typography.labelLarge)
        }
    }
}

@Composable
internal fun RadarAnswerContent(answer: RadarAnswer) {
    FlowRow(
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        RadarDimension.entries.forEach { dimension ->
            val score = answer.scores[dimension] ?: return@forEach
            Surface(shape = RoundedCornerShape(50), color = MaterialTheme.colorScheme.secondaryContainer) {
                Text(
                    "${dimension.emoji} ${dimension.label()} $score",
                    modifier = Modifier.padding(horizontal = 10.dp, vertical = 4.dp),
                    style = MaterialTheme.typography.labelLarge,
                )
            }
        }
    }
}
